import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { AdminRoute, UserRoute, TutorRoute, PublicRoute } from './components/RouteProtection';
import ErrorBoundary from './components/ErrorBoundary';
import { preloadResource } from './utils/performanceOptimizer';
import './index.css';

// Lazy load components for better performance
const LandingPage = lazy(() => import('./components/LandingPage'));
const SignUp = lazy(() => import('./components/SignUp'));
const Login = lazy(() => import('./components/Login'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const StudentDetails = lazy(() => import('./components/StudentDetails'));
const FirebaseTest = lazy(() => import('./components/FirebaseTest'));
const TutorSignUp = lazy(() => import('./components/TutorSignUp'));
const TutorLogin = lazy(() => import('./components/TutorLogin'));
const TutorDashboard = lazy(() => import('./components/TutorDashboard'));
const TutorResubmit = lazy(() => import('./components/TutorResubmit'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const WebRTCClassroom = lazy(() => import('./components/WebRTCClassroom'));
const PaymentPlans = lazy(() => import('./components/PaymentPlans'));
const TermsAndConditions = lazy(() => import('./components/TermsAndConditions'));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <div className="App">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/signup" element={<PublicRoute type="user"><SignUp /></PublicRoute>} />
                <Route path="/login" element={<PublicRoute type="user"><Login /></PublicRoute>} />
                <Route path="/test" element={<FirebaseTest />} />
                <Route path="/admin" element={<PublicRoute type="admin"><AdminLogin /></PublicRoute>} />
                <Route path="/admin-login" element={<PublicRoute type="admin"><AdminLogin /></PublicRoute>} />
                <Route path="/admin-dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                <Route 
                  path="/admin-dashboard/:tab"
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  } 
                />
                <Route path="/student-details/:studentId" element={<StudentDetails />} />
                <Route path="/tutor-signup" element={<PublicRoute type="tutor"><TutorSignUp /></PublicRoute>} />
                <Route path="/tutor-login" element={<PublicRoute type="tutor"><TutorLogin /></PublicRoute>} />
                <Route path="/tutor-resubmit" element={<TutorResubmit />} />
                <Route 
                  path="/tutor-dashboard" 
                  element={
                    <TutorRoute>
                      <TutorDashboard />
                    </TutorRoute>
                  } 
                />
                <Route 
                  path="/tutor-dashboard/settings/*"
                  element={
                    <TutorRoute>
                      <TutorDashboard />
                    </TutorRoute>
                  } 
                />
                <Route 
                  path="/tutor-dashboard/:tab"
                  element={
                    <TutorRoute>
                      <TutorDashboard />
                    </TutorRoute>
                  } 
                />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/classroom/:id" element={<WebRTCClassroom />} />
                <Route 
                  path="/payment-plans" 
                  element={
                    <UserRoute>
                      <PaymentPlans />
                    </UserRoute>
                  } 
                />
                <Route 
                  path="/dashboard" 
                  element={
                    <UserRoute>
                      <Dashboard />
                    </UserRoute>
                  } 
                />
                <Route 
                  path="/dashboard/settings/*"
                  element={
                    <UserRoute>
                      <Dashboard />
                    </UserRoute>
                  } 
                />
                <Route 
                  path="/dashboard/:tab"
                  element={
                    <UserRoute>
                      <Dashboard />
                    </UserRoute>
                  } 
                />
                <Route 
                  path="/terms-and-conditions" 
                  element={<TermsAndConditions />} 
                />
                <Route 
                  path="/tutor-terms-and-conditions" 
                  element={<TermsAndConditions />} 
                />
              </Routes>
            </Suspense>
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10B981',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 4000,
                  iconTheme: {
                    primary: '#EF4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
